import {
  AppliedQuickFilter_Customer,
  AppliedQuickFilter_New,
  AppliedQuickFilter_Sale,
  AppliedQuickFilter_StyleGroup,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/filter_section.types';
import {CustomerSession} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/session.types';
import {useHistory, useQueryParams} from '@aboutyou/router';
import {useCustomerSession} from '@aboutyou/user';
import {useMemo} from 'react';
import {getUpdatedFilterParams} from '../../../features/ProductStream/utils';

export enum QuickFilterSlugs {
  BRANDS_EXCLUDED = 'brandsExcluded',
  CUSTOMER = 'isForYou',
  NEW = 'isNewQuick',
  SALE = 'isSaleQuick',
  SUSTAINABLE = 'isSustainableQuick',
  STYLE_GROUP = 'styleGroup',
  FAVORITE_BRAND = 'favouriteBrand',
}

export const useQuickFilter = () => {
  const history = useHistory();
  const queryParams = useQueryParams();
  const search = queryParams.toString();
  const session = useCustomerSession();

  const appliedQuickFilterKeys = useMemo(
    () =>
      Array.from(queryParams.keys()).filter(
        key =>
          key !== QuickFilterSlugs.BRANDS_EXCLUDED &&
          Object.values(QuickFilterSlugs).includes(key as QuickFilterSlugs),
      ),
    [queryParams],
  );

  let appliedQuickFilterKey = appliedQuickFilterKeys[0];
  let appliedQuickFilterValue = appliedQuickFilterKey
    ? queryParams.get(appliedQuickFilterKey)
    : undefined;

  if (appliedQuickFilterKey === QuickFilterSlugs.CUSTOMER && !session) {
    appliedQuickFilterKey = undefined;
    appliedQuickFilterValue = undefined;
  }

  const excludedBrandsIds =
    queryParams.get(QuickFilterSlugs.BRANDS_EXCLUDED)?.split(',') ?? undefined;

  const favouriteBrandIds =
    queryParams.get(QuickFilterSlugs.FAVORITE_BRAND)?.split(',') ?? undefined;

  const appliedQuickFilter = getAppliedQuickFilter({
    appliedQuickFilterKey,
    appliedQuickFilterValue,
    excludedBrands: excludedBrandsIds,
    session,
  });

  // eslint-disable-next-line local-rules/prefer-use-query-params-callback
  const setQuickFilter = (
    slug?: string,
    value?: string,
    options?: {excludedBrands?: Array<string>; favouriteBrands: string[]},
  ) => {
    const excludedBrands = options?.excludedBrands;
    const update: Map<string, string[] | undefined> = new Map([]);

    if (appliedQuickFilterKey) {
      update.set(appliedQuickFilterKey, undefined);
    }

    Object.values(QuickFilterSlugs).forEach((quickFilterSlug: string) => {
      update.set(quickFilterSlug, undefined);
    });

    if (slug) {
      update.set(slug, value ? [value] : undefined);
    }

    if (excludedBrands) {
      update.set(QuickFilterSlugs.BRANDS_EXCLUDED, excludedBrands);
    } else {
      update.set(QuickFilterSlugs.BRANDS_EXCLUDED, undefined);
    }
    if (options?.favouriteBrands) {
      update.set(QuickFilterSlugs.FAVORITE_BRAND, options.favouriteBrands);
    }

    history.push({
      state: history.location.state,
      search: getUpdatedFilterParams({
        search,
        update,
      }),
    });
  };

  return {
    appliedQuickFilter,
    appliedQuickFilterKey,
    appliedQuickFilterValue,
    excludedBrands: excludedBrandsIds,
    favouriteBrands: favouriteBrandIds,
    personalizedBrandsFilterApplied: !!queryParams.get(
      QuickFilterSlugs.CUSTOMER,
    ),
    setQuickFilter,
  };
};

const getAppliedQuickFilter = ({
  appliedQuickFilterKey,
  appliedQuickFilterValue,
  excludedBrands,
  session,
}: {
  appliedQuickFilterKey?: string;
  appliedQuickFilterValue?: string | null;
  excludedBrands?: Array<string>;
  session?: CustomerSession;
}) => {
  switch (appliedQuickFilterKey) {
    case QuickFilterSlugs.CUSTOMER: {
      if (!session) return {};

      return {
        quickFilter: {
          $case: 'customer' as const,
          customer: {
            excludeBrands: excludedBrands?.map(brand => Number(brand)) ?? [],
            session,
          } as AppliedQuickFilter_Customer,
        },
      };
    }
    case QuickFilterSlugs.NEW: {
      return {
        quickFilter: {
          $case: 'new' as const,
          new: {} as AppliedQuickFilter_New,
        },
      };
    }
    case QuickFilterSlugs.SALE: {
      return {
        quickFilter: {
          $case: 'sale' as const,
          sale: {} as AppliedQuickFilter_Sale,
        },
      };
    }
    case QuickFilterSlugs.STYLE_GROUP: {
      const id = Number(appliedQuickFilterValue);

      if (!appliedQuickFilterValue || isNaN(id)) return {};

      return {
        quickFilter: {
          $case: 'styleGroup' as const,
          styleGroup: {styleGroupId: id} as AppliedQuickFilter_StyleGroup,
        },
      };
    }

    default:
      return {};
  }
};
